/**
 * @fileoverview Cash Movements Panel — markdown narrative + monthly chart +
 * chat refinement
 *
 * Renders the practitioner-facing cashflow narrative produced by the
 * `sum_bank_transactions_narrative` agent. Layout:
 *   - LEFT (2/3 width): the narrative itself, split into Overview /
 *     Counterparty sections (rendered as markdown) plus a deterministic
 *     monthly cashflow chart. The chart reads straight from
 *     `bankTransactionEntries` so it is always live and never depends on
 *     the LLM having finished a refresh cycle.
 *   - RIGHT (1/3 width): a chat sidebar where the practitioner can ask the
 *     agent to refine the narrative. Each refinement re-emits the FULL
 *     narrative on the left; the chat history captures the per-turn
 *     `changeSummary`.
 *
 * A categorisation status banner explains why counterparty sections might
 * still be missing (categorisation in progress, or finished without naming
 * any counterparties yet) so practitioners aren't left guessing.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { toast } from "sonner";
import {
  Loader2,
  Wand2,
  Send,
  RefreshCw,
  Info,
  Sparkles,
  AlertCircle,
  XCircle,
  MessageSquare,
  Mic,
  Square,
} from "lucide-react";
import { cn } from "@/utils/misc";
import {
  useChatVoiceInput,
  appendTranscript,
} from "@/ui/hooks/use-chat-voice-input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/ui/composed/sections/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

interface CashMovementsPanelProps {
  jobId: Id<"jobs">;
  isViewer?: boolean;
  /**
   * If set, the panel will pre-fill the chat input with a "tell me about X"
   * prompt and scroll to the chat sidebar. Used when the practitioner clicks
   * the "Generate narrative for this counterparty" button on the by-party tab.
   */
  initialChatPrompt?: string;
  onConsumeInitialChatPrompt?: () => void;
}

interface CategorySection {
  categoryKey: string;
  categoryLabel: string;
  body: string;
}

interface CounterpartySection {
  counterpartyName: string;
  qualificationReason: string;
  body: string;
}

interface CashflowNarrative {
  overview: string;
  /**
   * Deprecated — the agent no longer emits monthly markdown sections; the
   * UI renders a deterministic chart instead. Kept on the type so existing
   * persisted narratives don't break, but always ignored on render.
   */
  monthly?: unknown;
  category?: CategorySection[];
  counterparty?: CounterpartySection[];
  changeSummary?: string;
  generatedAt?: number;
  agentVersion?: number;
}

interface ChatMessageRow {
  _id: string;
  _creationTime: number;
  role: "user" | "assistant" | "system";
  body: string;
  createdAt: number;
  narrativeSnapshot?: unknown;
}

function formatRelativeTime(ts?: number): string | null {
  if (!ts) return null;
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export function CashMovementsPanel({
  jobId,
  isViewer,
  initialChatPrompt,
  onConsumeInitialChatPrompt,
}: CashMovementsPanelProps) {
  const { data: narrativeData, isLoading: narrativeLoading } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.bankTransactionsNarrative.publicMutations
        .getNarrativeForJob,
      { jobId },
    ),
  );

  const { data: categorisationStatus } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.bankTransactionsNarrative.publicMutations
        .getCategorisationStatus,
      { jobId },
    ),
  );

  const { data: chatMessages } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.bankTransactionsNarrative.publicMutations
        .listNarrativeChatMessages,
      { jobId },
    ),
  );

  const { mutateAsync: sendChat, isPending: isSending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactionsNarrative.publicMutations
        .sendNarrativeChatMessage,
    ),
  });

  const { mutateAsync: regenerate, isPending: isRegenerating } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactionsNarrative.publicMutations
        .regenerateNarrativeNow,
    ),
  });

  const { mutateAsync: cancelRun, isPending: isCancelling } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactionsNarrative.publicMutations
        .cancelNarrativeRun,
    ),
  });

  const [chatInput, setChatInput] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const chatLogRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  // Pre-fill chat input from a sibling tab's "ask about this counterparty"
  // action. Auto-opens the refine sheet so the prompt is visible. We consume
  // the prop once so re-renders don't keep re-applying it.
  useEffect(() => {
    if (initialChatPrompt) {
      setChatInput(initialChatPrompt);
      setChatOpen(true);
      onConsumeInitialChatPrompt?.();
      requestAnimationFrame(() => {
        chatInputRef.current?.focus();
        chatInputRef.current?.setSelectionRange(
          initialChatPrompt.length,
          initialChatPrompt.length,
        );
      });
    }
  }, [initialChatPrompt, onConsumeInitialChatPrompt]);

  // Auto-scroll the chat to the latest message (only meaningful when sheet is open).
  useEffect(() => {
    if (chatOpen && chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [chatMessages?.length, chatOpen]);

  const narrative = (narrativeData?.narrative ??
    null) as CashflowNarrative | null;
  const isAgentRunning = narrativeData?.status === "running";

  const handleSend = useCallback(async () => {
    const message = chatInput.trim();
    if (!message) return;
    try {
      await sendChat({ jobId, message });
      setChatInput("");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message",
      );
    }
  }, [chatInput, jobId, sendChat]);

  const handleRegenerate = useCallback(async () => {
    try {
      await regenerate({ jobId });
      toast.success("Regenerating cashflow narrative…");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to start regeneration",
      );
    }
  }, [jobId, regenerate]);

  const handleCancelAndRestart = useCallback(async () => {
    try {
      const result = await cancelRun({ jobId });
      if (result.cancelledTaskId === null) {
        toast.info("No active run to cancel");
        return;
      }
      toast.success(
        result.expiredSessions > 0
          ? `Run cancelled · ${result.expiredSessions} session(s) reset`
          : "Run cancelled",
      );
      await regenerate({ jobId });
      toast.success("Restarting on the latest agent version…");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to cancel run");
    }
  }, [jobId, cancelRun, regenerate]);

  const lastRunRel = useMemo(
    () => formatRelativeTime(narrativeData?.lastRunAt),
    [narrativeData?.lastRunAt],
  );

  if (narrativeLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  const chatMessageCount = chatMessages?.length ?? 0;
  const categories = narrative?.category ?? [];
  const counterparties = narrative?.counterparty ?? [];

  const headerBlock = (
    <>
      <NarrativeHeader
        isAgentRunning={isAgentRunning}
        liveText={narrativeData?.liveText}
        lastRunRel={lastRunRel}
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
        onCancelAndRestart={handleCancelAndRestart}
        isCancelling={isCancelling}
        isViewer={isViewer}
        hasNarrative={narrative !== null}
        onOpenRefine={() => setChatOpen(true)}
        chatMessageCount={chatMessageCount}
      />

      <CategorisationStatusBanner
        status={categorisationStatus}
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
        isAgentRunning={isAgentRunning}
        isViewer={isViewer}
      />
    </>
  );

  return (
    <>
      {narrative !== null ? (
        <Tabs defaultValue="overview" className="mx-auto w-full max-w-4xl">
          <div className="sticky top-[196px] z-[8] flex flex-col gap-3 bg-muted pb-3 pt-3 shadow-[0_-24px_0_0_hsl(var(--muted))]">
            {headerBlock}

            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="category" className="gap-1.5">
                By Category
                {categories.length > 0 && (
                  <Badge variant="soft" color="gray" size="sm">
                    {categories.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="counterparty" className="gap-1.5">
                By Counterparty
                {counterparties.length > 0 && (
                  <Badge variant="soft" color="gray" size="sm">
                    {counterparties.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview">
            <Section title="Overview" markdown={narrative.overview} />
          </TabsContent>

          <TabsContent value="category">
            {categories.length > 0 ? (
              <div className="space-y-3">
                {categories.map((c) => (
                  <CategoryCard
                    key={c.categoryKey}
                    label={c.categoryLabel}
                    markdown={c.body}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed py-12 text-center text-xs text-muted-foreground">
                No category sections generated yet.
              </div>
            )}
          </TabsContent>

          <TabsContent value="counterparty">
            <CounterpartyTabContent counterparties={counterparties} />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
          {headerBlock}

          <NarrativeEmptyState
            isAgentRunning={isAgentRunning}
            onRegenerate={handleRegenerate}
            isRegenerating={isRegenerating}
            isViewer={isViewer}
          />
        </div>
      )}

      <RefineNarrativeSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        chatMessages={chatMessages ?? []}
        chatInput={chatInput}
        onChatInputChange={setChatInput}
        onSend={handleSend}
        isSending={isSending}
        isAgentRunning={isAgentRunning}
        isViewer={isViewer}
        chatLogRef={chatLogRef}
        chatInputRef={chatInputRef}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function NarrativeHeader({
  isAgentRunning,
  liveText,
  lastRunRel,
  onRegenerate,
  isRegenerating,
  onCancelAndRestart,
  isCancelling,
  isViewer,
  hasNarrative,
  onOpenRefine,
  chatMessageCount,
}: {
  isAgentRunning: boolean;
  liveText?: string;
  lastRunRel: string | null;
  onRegenerate: () => void;
  isRegenerating: boolean;
  onCancelAndRestart: () => void;
  isCancelling: boolean;
  isViewer?: boolean;
  hasNarrative: boolean;
  onOpenRefine: () => void;
  chatMessageCount: number;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Wand2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h3 className="truncate text-base font-semibold">
            Cash Movements Narrative
          </h3>
          {isAgentRunning && (
            <Badge variant="soft" color="green" size="sm">
              Updating…
            </Badge>
          )}
          {!isAgentRunning && lastRunRel && hasNarrative && (
            <span className="text-[11px] text-muted-foreground">
              Updated {lastRunRel}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenRefine}
            disabled={isViewer}
            className="relative gap-1.5"
          >
            <MessageSquare className="h-3 w-3" />
            Refine Narrative
            {chatMessageCount > 0 && (
              <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium tabular-nums text-muted-foreground">
                {chatMessageCount}
              </span>
            )}
            {isAgentRunning && (
              <span className="absolute -right-1 -top-1 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
            )}
          </Button>
          {isAgentRunning && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancelAndRestart}
              disabled={isViewer || isCancelling}
              className="gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
              title="Cancel the in-flight run and start a fresh one on the latest agent version"
            >
              {isCancelling ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              Cancel & restart
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerate}
            disabled={isViewer || isRegenerating || isAgentRunning}
            className="gap-1.5"
          >
            {isRegenerating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            {hasNarrative ? "Regenerate" : "Generate now"}
          </Button>
        </div>
      </div>
      {isAgentRunning && liveText && liveText.trim().length > 0 && (
        <p className="truncate pl-6 text-[11px] italic text-muted-foreground">
          {liveText}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Refine narrative — slide-out chat sheet
// ---------------------------------------------------------------------------

function RefineNarrativeSheet({
  open,
  onOpenChange,
  chatMessages,
  chatInput,
  onChatInputChange,
  onSend,
  isSending,
  isAgentRunning,
  isViewer,
  chatLogRef,
  chatInputRef,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  chatMessages: ChatMessageRow[];
  chatInput: string;
  onChatInputChange: (v: string) => void;
  onSend: () => void;
  isSending: boolean;
  isAgentRunning: boolean;
  isViewer?: boolean;
  chatLogRef: React.RefObject<HTMLDivElement>;
  chatInputRef: React.RefObject<HTMLTextAreaElement>;
}) {
  const insertSuggestion = (text: string) => {
    onChatInputChange(text);
    requestAnimationFrame(() => {
      chatInputRef.current?.focus();
      chatInputRef.current?.setSelectionRange(text.length, text.length);
    });
  };

  const voice = useChatVoiceInput({
    onFinal: (transcript) =>
      onChatInputChange(appendTranscript(chatInput, transcript)),
  });

  const hasText = chatInput.trim().length > 0;
  // Action-button mode, in precedence order.
  type ComposerButtonMode =
    "sending" | "processing" | "listening" | "send" | "mic";
  const buttonMode: ComposerButtonMode = isSending
    ? "sending"
    : voice.isProcessing
      ? "processing"
      : voice.isListening
        ? "listening"
        : hasText || !voice.isEnabled
          ? "send"
          : "mic";
  const displayValue = voice.isActive
    ? appendTranscript(chatInput, voice.interim)
    : chatInput;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col p-0 sm:max-w-md"
      >
        <SheetHeader className="space-y-1 border-b px-5 py-4">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            Refine narrative
            {isAgentRunning && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-normal text-emerald-600 dark:text-emerald-400">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                Working
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="text-left text-[11px]">
            Ask the agent to expand a section, flag a pattern, or rewrite a
            paragraph. The narrative updates in place behind this panel.
          </SheetDescription>
        </SheetHeader>

        <div
          ref={chatLogRef}
          className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
        >
          {chatMessages.length === 0 && (
            <div className="space-y-3 px-1 py-6 text-xs text-muted-foreground">
              <p>No refinements yet — try one of these to get started:</p>
              <div className="flex flex-col gap-1.5">
                {[
                  "Expand the section on Thompson Family Trust.",
                  "What's driving the spike in receipts in mid-2023?",
                  "Flag any round-number transfers I should investigate.",
                ].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => insertSuggestion(s)}
                    className="rounded-md bg-muted/50 px-3 py-2 text-left text-xs text-foreground transition-colors hover:bg-muted"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {chatMessages.map((m) => (
            <ChatRow
              key={m._id}
              role={m.role}
              body={m.body}
              createdAt={m.createdAt}
            />
          ))}
          {isAgentRunning && (
            <div className="flex items-center gap-2 px-2 text-xs italic text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Agent is updating the narrative…
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSend();
          }}
          className="border-t bg-background p-3"
        >
          <div className="flex gap-2">
            <textarea
              ref={chatInputRef}
              value={displayValue}
              onChange={(e) => onChatInputChange(e.target.value)}
              placeholder={
                isViewer
                  ? "Read-only access — chat disabled"
                  : voice.isListening
                    ? "Listening — start speaking…"
                    : "Ask the agent to refine…"
              }
              rows={2}
              disabled={isViewer || isSending}
              readOnly={voice.isActive}
              className="flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
            />
            <Button
              type={buttonMode === "send" ? "submit" : "button"}
              size="sm"
              variant={buttonMode === "listening" ? "destructive" : "default"}
              disabled={
                isViewer ||
                buttonMode === "sending" ||
                buttonMode === "processing" ||
                (buttonMode === "send" && !hasText)
              }
              onClick={
                buttonMode === "mic" || buttonMode === "listening"
                  ? voice.toggle
                  : undefined
              }
              aria-pressed={buttonMode === "listening"}
              className="gap-1.5 self-end"
            >
              {buttonMode === "sending" || buttonMode === "processing" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : buttonMode === "listening" ? (
                <Square className="h-3 w-3 fill-current" />
              ) : buttonMode === "mic" ? (
                <Mic className="h-3 w-3" />
              ) : (
                <Send className="h-3 w-3" />
              )}
              {buttonMode === "listening"
                ? "Stop"
                : buttonMode === "processing"
                  ? "…"
                  : buttonMode === "mic"
                    ? "Dictate"
                    : "Send"}
            </Button>
          </div>
          <AiDisclaimer className="mt-1 text-center" />
        </form>
      </SheetContent>
    </Sheet>
  );
}

function NarrativeEmptyState({
  isAgentRunning,
  onRegenerate,
  isRegenerating,
  isViewer,
}: {
  isAgentRunning: boolean;
  onRegenerate: () => void;
  isRegenerating: boolean;
  isViewer?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border bg-zinc-50 py-12 text-center dark:bg-zinc-900">
      <div className="rounded-full bg-muted/50 p-4">
        <Wand2 className="h-8 w-8 text-muted-foreground/40" />
      </div>
      <div className="max-w-md space-y-2">
        <p className="text-sm font-medium">No narrative yet</p>
        <p className="text-xs text-muted-foreground">
          The cashflow narrative is generated automatically a few minutes after
          categorisation settles. Click{" "}
          <span className="font-medium">Generate now</span> to skip the wait.
        </p>
        <p className="text-xs text-muted-foreground">
          The agent will write an overview, a section for each transaction
          category (e.g. utilities, employee costs), and a section for each
          qualified counterparty (top&nbsp;20 by total amount and any
          counterparty with a single transaction over $50,000).
        </p>
      </div>
      {isAgentRunning ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Agent is writing the
          first draft…
        </div>
      ) : (
        <Button
          variant="default"
          size="sm"
          onClick={onRegenerate}
          disabled={isViewer || isRegenerating}
          className="gap-1.5"
        >
          {isRegenerating ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Wand2 className="h-3 w-3" />
          )}
          Generate now
        </Button>
      )}
    </div>
  );
}

function CounterpartyTabContent({
  counterparties,
}: {
  counterparties: CounterpartySection[];
}) {
  const classified = useMemo(
    () =>
      counterparties.map((c) => ({
        ...c,
        segments: parseEyebrowSegments(c.qualificationReason),
        kind: classifyCounterparty(c.qualificationReason),
      })),
    [counterparties],
  );

  const counts = useMemo(() => {
    const c: Record<CounterpartyKind | "all", number> = {
      all: classified.length,
      revenue: 0,
      payments: 0,
      large: 0,
      default: 0,
    };
    for (const item of classified) c[item.kind] += 1;
    return c;
  }, [classified]);

  type FilterId = CounterpartyKind | "all";
  const [filter, setFilter] = useState<FilterId>("all");

  const visible = useMemo(
    () =>
      filter === "all"
        ? classified
        : classified.filter((c) => c.kind === filter),
    [classified, filter],
  );

  if (counterparties.length === 0) {
    return (
      <div className="rounded-lg border border-dashed py-12 text-center text-xs text-muted-foreground">
        No counterparty sections generated yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {classified.length > 1 && (
        <CounterpartyFilterBar
          counts={counts}
          value={filter}
          onChange={setFilter}
        />
      )}
      {visible.map((c) => (
        <CounterpartyCard
          key={c.counterpartyName}
          name={c.counterpartyName}
          segments={c.segments}
          kind={c.kind}
          markdown={c.body}
        />
      ))}
      {visible.length === 0 && (
        <div className="rounded-lg border border-dashed py-6 text-center text-xs text-muted-foreground">
          No counterparties match this filter.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Counterparty cards — typography-first, minimal chrome
// ---------------------------------------------------------------------------

type CounterpartyKind = "revenue" | "payments" | "large" | "default";

const KIND_FILTER_LABEL: Record<CounterpartyKind, string> = {
  revenue: "Revenue",
  payments: "Payments",
  large: "Large txns",
  default: "Other",
};

/**
 * Map a qualification reason string (free-text from the agent, but bounded to
 * the deterministic templates in `narrative_listQualifiedCounterparties`) to
 * a visual kind for the filter bar.
 */
function classifyCounterparty(reason: string | undefined): CounterpartyKind {
  const r = (reason ?? "").toLowerCase();
  if (r.includes("revenue") || r.includes("receipts")) return "revenue";
  if (r.includes("payment")) return "payments";
  if (
    r.includes("largest single") ||
    r.includes("transaction over") ||
    r.includes("single large")
  )
    return "large";
  return "default";
}

/**
 * Split a qualification reason like
 * "Top 2 by total value; related party; largest single transaction $83,466"
 * into individual eyebrow segments so we can highlight the related-party
 * portion without rendering a noisy row of pill badges.
 */
function parseEyebrowSegments(reason: string | undefined): string[] {
  if (!reason) return [];
  return reason
    .split(/[;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function CounterpartyCard({
  name,
  segments,
  markdown,
}: {
  name: string;
  segments: string[];
  kind: CounterpartyKind;
  markdown: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-5 transition-shadow">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h5 className="text-[15px] font-semibold leading-tight">{name}</h5>
        {segments.length > 0 && (
          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] tracking-wide text-muted-foreground">
            {segments.map((seg, i) => (
              <span key={i} className="inline-flex items-center gap-1.5">
                {i > 0 && <span className="opacity-40">·</span>}
                <span>{seg}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="prose prose-sm mt-3 max-w-none dark:prose-invert prose-p:my-2 prose-p:leading-relaxed prose-strong:font-semibold prose-strong:text-foreground">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
      </div>
    </div>
  );
}

function CategoryCard({
  label,
  markdown,
}: {
  label: string;
  markdown: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <h5 className="text-[15px] font-semibold leading-tight">{label}</h5>
      <div className="prose prose-sm mt-3 max-w-none dark:prose-invert prose-p:my-2 prose-p:leading-relaxed prose-strong:font-semibold prose-strong:text-foreground">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
      </div>
    </div>
  );
}

function CounterpartyFilterBar({
  counts,
  value,
  onChange,
}: {
  counts: Record<CounterpartyKind | "all", number>;
  value: CounterpartyKind | "all";
  onChange: (v: CounterpartyKind | "all") => void;
}) {
  const options: Array<{ id: CounterpartyKind | "all"; label: string }> = [
    { id: "all", label: "All" },
    ...(["revenue", "payments", "large", "default"] as const)
      .filter((k) => counts[k] > 0)
      .map((k) => ({ id: k, label: KIND_FILTER_LABEL[k] })),
  ];
  if (options.length <= 1) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px]">
      {options.map((opt) => {
        const active = value === opt.id;
        const count = counts[opt.id] ?? 0;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              "inline-flex items-center gap-1 transition-colors",
              active
                ? "font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "border-b border-transparent pb-0.5",
                active && "border-foreground",
              )}
            >
              {opt.label}
            </span>
            <span className="tabular-nums opacity-60">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

interface CategorisationStatus {
  hasTask: boolean;
  status?: string;
  totalTransactions: number;
  totalNamedParties: number;
  uncategorisedTransactions: number;
  cacheUpdatedAt?: number;
  narrativeGeneratedAt?: number;
  narrativeOutOfDate: boolean;
}

function CategorisationStatusBanner({
  status,
  onRegenerate,
  isRegenerating,
  isAgentRunning,
  isViewer,
}: {
  status: CategorisationStatus | undefined;
  onRegenerate: () => void;
  isRegenerating: boolean;
  isAgentRunning: boolean;
  isViewer?: boolean;
}) {
  if (!status || !status.hasTask) return null;

  const hasParties = status.totalNamedParties > 0;
  const isCategorising = status.status === "running";

  // Three meaningful states:
  // 1. Categorisation still running    → amber banner, no CTA (auto regen on completion)
  // 2. Narrative is stale relative to the cache  → amber banner with regen CTA
  // 3. Everything in sync               → compact info chip (no banner)
  if (isCategorising) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            Categorisation in progress ·{" "}
            {status.totalTransactions.toLocaleString()} transactions,{" "}
            {status.totalNamedParties.toLocaleString()} counterparties so far
          </div>
          <p className="mt-0.5 text-[11px] opacity-80">
            The cashflow narrative will refresh automatically a few minutes
            after categorisation settles.
          </p>
        </div>
      </div>
    );
  }

  if (status.narrativeOutOfDate) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            Narrative is missing counterparty analysis ·{" "}
            {status.totalNamedParties.toLocaleString()} counterparties have been
            categorised since the last narrative refresh.
          </div>
          <p className="mt-0.5 text-[11px] opacity-80">
            Regenerate to pull through the per-counterparty sections.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 gap-1 border-amber-300 px-2 text-[11px] hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900/30"
          onClick={onRegenerate}
          disabled={isViewer || isRegenerating || isAgentRunning}
        >
          {isRegenerating ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Regenerate
        </Button>
      </div>
    );
  }

  if (status.totalTransactions === 0) return null;

  return (
    <div className="flex items-center gap-2 px-1 text-[11px] text-muted-foreground">
      <Info className="h-3 w-3" />
      <span>
        {status.totalTransactions.toLocaleString()} transactions ·{" "}
        {hasParties
          ? `${status.totalNamedParties.toLocaleString()} counterparties identified`
          : "counterparties pending"}
        {status.uncategorisedTransactions > 0 && (
          <>
            {" "}
            · {status.uncategorisedTransactions.toLocaleString()} uncategorised
          </>
        )}
      </span>
    </div>
  );
}

function Section({ title, markdown }: { title: string; markdown: string }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
      </div>
    </div>
  );
}

function ChatRow({
  role,
  body,
  createdAt,
}: {
  role: "user" | "assistant" | "system";
  body: string;
  createdAt: number;
}) {
  if (role === "system") {
    return (
      <div className="my-1 flex items-center gap-2 px-1">
        <div className="h-px flex-1 bg-border" />
        <span className="px-2 text-[10px] italic text-muted-foreground">
          {body}
          {formatRelativeTime(createdAt)
            ? ` · ${formatRelativeTime(createdAt)}`
            : ""}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>
    );
  }
  const isUser = role === "user";
  return (
    <div
      className={cn(
        "flex flex-col gap-1",
        isUser ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "max-w-[90%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-xs",
          isUser
            ? "bg-primary text-primary-foreground"
            : "border bg-background",
        )}
      >
        {body}
      </div>
      <span className="px-1 text-[10px] text-muted-foreground">
        {formatRelativeTime(createdAt) ?? ""}
      </span>
    </div>
  );
}
